import { Select as BaseSelect } from '@base-ui/react/select';
import { useMemo, useState } from 'react';
import { Pf2eBadge, type Pf2eBadgeKind } from './Pf2eBadge';
import { cx } from './utils';

export type SelectOption = {
  value: string;
  label: string;
  /** Compact PF2e metadata rendered as a shared badge inside a popup option. */
  badge?: { kind?: Pf2eBadgeKind; label: string };
  /** Put the badge into the beginning of the option heading instead of its trailing edge. */
  badgePlacement?: 'before' | 'after';
  /** Secondary compact metadata rendered after the label. */
  meta?: string;
  /** Optional explanatory line rendered only inside the popup. */
  description?: string;
  searchText?: string;
  disabled?: boolean;
};

export interface SelectProps {
  options: SelectOption[];
  value?: string | null;
  defaultValue?: string | null;
  onValueChange?: (value: string | null) => void;
  placeholder?: string;
  label?: string;
  ariaLabel?: string;
  disabled?: boolean;
  name?: string;
  className?: string;
  /** Raise the portalled list above the Dialog that owns its trigger. */
  portalLayer?: 'default' | 'modal';
  /** Let a short, known option set grow to its full height instead of scrolling. */
  fitContent?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  noOptionsLabel?: string;
}

export function Select({
  options,
  value,
  defaultValue,
  onValueChange,
  placeholder,
  label,
  ariaLabel,
  disabled = false,
  name,
  className,
  portalLayer = 'default',
  fitContent = false,
  searchable = false,
  searchPlaceholder,
  noOptionsLabel,
}: SelectProps) {
  const [search, setSearch] = useState('');
  const filteredOptions = useMemo(() => {
    const needle = search.trim().toLocaleLowerCase();
    if (!searchable || !needle) return options;
    return options.filter(option => (
      `${option.label} ${option.badge?.label ?? ''} ${option.meta ?? ''} ${option.description ?? ''} ${option.searchText ?? ''}`.toLocaleLowerCase().includes(needle)
    ));
  }, [options, search, searchable]);

  return (
    <BaseSelect.Root
      value={value}
      defaultValue={defaultValue}
      onValueChange={(nextValue) => {
        setSearch('');
        onValueChange?.(nextValue);
      }}
      disabled={disabled}
      name={name}
      items={filteredOptions}
    >
      <div className="ui-select">
        {label ? <BaseSelect.Label className="ui-select__label">{label}</BaseSelect.Label> : null}
        <BaseSelect.Trigger className={cx('ui-select__trigger', className)} aria-label={ariaLabel ?? label}>
          <BaseSelect.Value placeholder={placeholder} />
          <BaseSelect.Icon className="ui-select__icon" aria-hidden="true">
            <span className="ui-select__chevron" />
          </BaseSelect.Icon>
        </BaseSelect.Trigger>
      </div>
      <BaseSelect.Portal>
        <BaseSelect.Positioner
          align="start"
          collisionPadding={8}
          className={cx('ui-positioner', portalLayer === 'modal' && 'ui-positioner--modal')}
          side="bottom"
          sideOffset={4}
        >
          <BaseSelect.Popup className={cx(
            'ui-select__popup',
            fitContent && 'ui-select__popup--fit-content',
          )}>
            {searchable ? (
              <input
                className="ui-select__search"
                type="search"
                value={search}
                onChange={event => setSearch(event.target.value)}
                onKeyDown={event => {
                  if (event.key === 'Escape') {
                    setSearch('');
                    return;
                  }
                  event.stopPropagation();
                }}
                onPointerDown={event => event.stopPropagation()}
                placeholder={searchPlaceholder}
                aria-label={searchPlaceholder}
              />
            ) : null}
            <BaseSelect.List className="ui-select__list">
              {filteredOptions.map((option) => (
                <BaseSelect.Item
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  disabled={option.disabled}
                  className="ui-select__item"
                >
                  <span className="ui-select__item-copy">
                    <span className="ui-select__item-heading">
                      {option.badge && option.badgePlacement === 'before' ? <Pf2eBadge kind={option.badge.kind}>{option.badge.label}</Pf2eBadge> : null}
                      <BaseSelect.ItemText>{option.label}</BaseSelect.ItemText>
                      {option.meta ? <small>{option.meta}</small> : null}
                    </span>
                    {option.description ? <small className="ui-select__item-description">{option.description}</small> : null}
                  </span>
                  {option.badge && option.badgePlacement !== 'before' ? <Pf2eBadge kind={option.badge.kind}>{option.badge.label}</Pf2eBadge> : null}
                </BaseSelect.Item>
              ))}
              {filteredOptions.length === 0 ? (
                <div className="ui-select__empty" role="status">{noOptionsLabel}</div>
              ) : null}
            </BaseSelect.List>
          </BaseSelect.Popup>
        </BaseSelect.Positioner>
      </BaseSelect.Portal>
    </BaseSelect.Root>
  );
}
